import {
  getUserUnlockResourceAccessDescriptor,
  getUserUnlockResourceCTAHref,
} from "@/app/components/cards/ResourceNotAvailablePlaceholder";
import { QuestionDifficulty } from "@/enums/contentTypes";
import { UserAccess } from "@/lib/nextAuth/userPermissions";
import { getUserVerificationStatus } from "@/lib/utils/accessChecks";
import { Button, Loader } from "@mantine/core";
import { LockRounded } from "@mui/icons-material";
import { roundToWhole } from "@utils/helpers";
import { titleToUrl } from "@utils/routing";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { setNotification } from "../../../../../../../../components/notifications";
import {
  getTestLaMarks,
  getTestMcMarks,
} from "../../../test-taker/components/TestMetrics";
import { convertTestTypeToNiceName } from "./TestPreviewStep";
import { LimitType, MockExamType, TestFormats } from "./types";

interface StartTestButtonProps {
  userAccess: UserAccess;
  subjectNameLower: string;
  subjectDocumentId: string;
  subTopicDocumentIds: string[];
  testType: TestFormats.custom | MockExamType;
  selectedDifficulties: QuestionDifficulty[];
  limitType: LimitType;
  limitValue: number;
  mcAbsoluteProportion: number;
  size?: "sm" | "md";
}

export default function StartTestButton({
  userAccess,
  subjectNameLower,
  subjectDocumentId,
  subTopicDocumentIds,
  testType,
  selectedDifficulties,
  limitType,
  limitValue,
  mcAbsoluteProportion,
  size = "md",
}: StartTestButtonProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const [isBuilding, setIsBuilding] = useState(false);
  const userUnlockResourceAccessDescriptor =
    getUserUnlockResourceAccessDescriptor(
      session,
      getUserVerificationStatus(session)
    );

  return (
    <Button
      component={userAccess !== "Full Access" ? Link : undefined}
      href={
        userAccess !== "Full Access"
          ? getUserUnlockResourceCTAHref(userUnlockResourceAccessDescriptor)
          : "#"
      }
      size={size}
      leftSection={
        userAccess === "LOADING" ? (
          <Loader size="sm" type="dots" color="white" />
        ) : userAccess === "No Access" ? (
          <LockRounded style={{ width: 20, height: 20 }} />
        ) : undefined
      }
      my="xl"
      tt="capitalize"
      color="darkblue"
      loading={isBuilding}
      onClick={async () => {
        if (userAccess !== "Full Access") {
          return;
        }
        const createdTestId = await buildCustomTest({
          testFormat: convertTestTypeToTestFormat(testType),
          subTopicDocumentIds,
          limitType,
          limitValue,
          mcAbsoluteProportion,
          subjectNameLower,
          subjectDocumentId,
          selectedDifficulties,
          setIsBuilding,
        });
        if (createdTestId) {
          router.push(
            `/ib/${titleToUrl(
              subjectNameLower
            )}/test-taker/?testId=${createdTestId}`
          );
        }
      }}
      disabled={
        ["LOADING", "No Access"].includes(userAccess)
          ? false
          : subTopicDocumentIds.length === 0
      }
    >
      {userAccess === "LOADING"
        ? "Checking access..."
        : userAccess === "Full Access"
        ? `Generate ${convertTestTypeToNiceName(testType)}`
        : userUnlockResourceAccessDescriptor.replace("unlock", "generate")}
    </Button>
  );
}

function convertTestTypeToTestFormat(
  testType: TestFormats.custom | MockExamType
): TestFormats {
  switch (testType) {
    case "slMcPaper":
    case "hlMcPaper":
    case "slLaPaper":
    case "hlLaPaper":
      return TestFormats.mockExam;
    default:
      return TestFormats.custom;
  }
}

function convertTestLimitsToMarks(
  limitType: LimitType,
  limitValue: number,
  mcAbsoluteProportion: number
): { marks: number; mcMarks: number } {
  if (limitType === "questions") {
    return {
      marks: roundToWhole(limitValue),
      mcMarks: roundToWhole(mcAbsoluteProportion),
    };
  }
  // convert the number of minutes of MC to number of marks
  const mcMarks = getTestMcMarks(mcAbsoluteProportion * 60);
  // conver the number of minutes of LA to number of marks
  const laAbsoluteProporition = limitValue - mcAbsoluteProportion;
  const laMarks = getTestLaMarks(laAbsoluteProporition * 60);

  return {
    marks: roundToWhole(mcMarks + laMarks),
    mcMarks: roundToWhole(mcMarks),
  };
}

async function buildCustomTest({
  testFormat,
  subTopicDocumentIds,
  limitType,
  limitValue,
  mcAbsoluteProportion,
  subjectNameLower,
  subjectDocumentId,
  selectedDifficulties,
  setIsBuilding,
}: {
  testFormat: TestFormats;
  subTopicDocumentIds: string[];
  limitType: LimitType;
  limitValue: number;
  mcAbsoluteProportion: number;
  subjectNameLower: string;
  subjectDocumentId: string;
  selectedDifficulties: QuestionDifficulty[];
  setIsBuilding: (value: boolean) => void;
}) {
  setIsBuilding(true);
  try {
    // Convert values to marks if limitType is "time"
    const { marks, mcMarks } = convertTestLimitsToMarks(
      limitType,
      limitValue,
      mcAbsoluteProportion
    );

    const res = await fetch("/api/strapi/createCustomTest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        testFormat,
        subjectDocumentId,
        subTopicDocumentIds,
        difficulties: selectedDifficulties,
        marks,
        mcMarks,
      }),
    });
    const response = await res.json();

    if (!res.ok) {
      throw new Error(response.msg || "Failed to create test");
    }

    return response.createdTestId;
  } catch (error: any) {
    setNotification(
      "Custom Test",
      false,
      error.message || "Failed to create test. Please try again.",
      "error",
      true
    );
  } finally {
    setIsBuilding(false);
  }
}
